/**
 * CountdownTimer - FOMO Component
 *
 * Flip-style animated countdown timer for Genesis Node launches.
 * Uses HVME mediakit colors - 10X Zama, uniquely HVME!
 */

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

interface TimeUnit {
  value: number;
  label: string;
}

interface CountdownTimerProps {
  /** Target date for countdown */
  targetDate: Date;
  /** Title displayed above countdown */
  title?: string;
  /** Subtitle displayed below title */
  subtitle?: string;
  /** Callback when countdown reaches zero */
  onComplete?: () => void;
  /** Size variant */
  variant?: "hero" | "compact" | "inline";
  /** Custom class name */
  className?: string;
}

function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}

function calculateTimeLeft(targetDate: Date) {
  const difference = targetDate.getTime() - Date.now();

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isComplete: true };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
    isComplete: false,
  };
}

/**
 * Single digit flip animation
 */
function FlipDigit({ value, size }: { value: number; size: "hero" | "compact" | "inline" }) {
  const sizeClasses = {
    hero: "w-16 h-20 text-4xl md:w-20 md:h-24 md:text-5xl",
    compact: "w-10 h-12 text-xl",
    inline: "w-6 h-7 text-sm",
  };

  return (
    <div className={`relative ${sizeClasses[size]} overflow-hidden`}>
      <AnimatePresence mode="popLayout">
        <motion.div
          key={value}
          initial={{ y: -40, opacity: 0, rotateX: -90 }}
          animate={{ y: 0, opacity: 1, rotateX: 0 }}
          exit={{ y: 40, opacity: 0, rotateX: 90 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="absolute inset-0 flex items-center justify-center font-mono font-bold text-hvme-near-white bg-hvme-deep-ink/80 rounded-lg border border-hvme-aurora-lilac/20 backdrop-blur-sm"
          style={{
            boxShadow: "0 4px 20px rgba(230, 190, 250, 0.15), inset 0 1px 0 rgba(255,255,255,0.1)",
          }}
        >
          {value.toString().padStart(2, "0")}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/**
 * Time unit display (value + label)
 */
function TimeUnitDisplay({
  value,
  label,
  size,
}: TimeUnit & { size: "hero" | "compact" | "inline" }) {
  const labelSizes = {
    hero: "text-xs md:text-sm mt-2",
    compact: "text-xs mt-1",
    inline: "text-[10px] mt-0.5",
  };

  return (
    <div className="flex flex-col items-center">
      <FlipDigit value={value} size={size} />
      <span className={`uppercase tracking-wider text-hvme-text-muted ${labelSizes[size]}`}>
        {label}
      </span>
    </div>
  );
}

/**
 * Separator between time units
 */
function Separator({ size }: { size: "hero" | "compact" | "inline" }) {
  const sizeClasses = {
    hero: "text-3xl md:text-4xl h-20 md:h-24",
    compact: "text-xl h-12",
    inline: "text-sm h-7",
  };

  return (
    <div className={`flex items-center justify-center ${sizeClasses[size]}`}>
      <motion.span
        className="text-hvme-aurora-lilac font-bold"
        animate={{ opacity: [1, 0.3, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
      >
        :
      </motion.span>
    </div>
  );
}

export function CountdownTimer({
  targetDate,
  title = "GENESIS ALLOCATION CLOSES IN",
  subtitle,
  onComplete,
  variant = "hero",
  className = "",
}: CountdownTimerProps) {
  const { days, hours, minutes, seconds, isComplete } = useCountdown(targetDate);

  useEffect(() => {
    if (isComplete && onComplete) {
      onComplete();
    }
  }, [isComplete, onComplete]);

  if (isComplete) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`text-center ${className}`}
      >
        <span className="text-2xl md:text-4xl font-bold text-hvme-aurora-lilac animate-pulse">
          ALLOCATION CLOSED
        </span>
      </motion.div>
    );
  }

  // Inline variant - minimal display
  if (variant === "inline") {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <span className="text-xs text-hvme-text-muted mr-1">Closes in:</span>
        <span className="font-mono text-sm text-hvme-aurora-lilac">
          {days > 0 && `${days}d `}
          {hours.toString().padStart(2, "0")}:{minutes.toString().padStart(2, "0")}:
          {seconds.toString().padStart(2, "0")}
        </span>
      </div>
    );
  }

  return (
    <div className={`text-center ${className}`}>
      {/* Title */}
      {title && (
        <motion.h3
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`font-bold tracking-wider text-hvme-text-secondary mb-4 ${
            variant === "hero" ? "text-sm md:text-base" : "text-xs"
          }`}
        >
          {title}
        </motion.h3>
      )}

      {/* Countdown Display */}
      <div className="flex items-start justify-center gap-2 md:gap-4">
        {days > 0 && (
          <>
            <TimeUnitDisplay value={days} label="Days" size={variant} />
            <Separator size={variant} />
          </>
        )}
        <TimeUnitDisplay value={hours} label="Hours" size={variant} />
        <Separator size={variant} />
        <TimeUnitDisplay value={minutes} label="Mins" size={variant} />
        <Separator size={variant} />
        <TimeUnitDisplay value={seconds} label="Secs" size={variant} />
      </div>

      {/* Subtitle */}
      {subtitle && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={`text-hvme-text-muted mt-4 ${
            variant === "hero" ? "text-sm md:text-base" : "text-xs"
          }`}
        >
          {subtitle}
        </motion.p>
      )}

      {/* Urgency indicator when time is low */}
      {days === 0 && hours < 24 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-4"
        >
          <span className="px-4 py-1.5 text-xs font-bold rounded-full bg-hvme-aurora-lilac/20 text-hvme-aurora-lilac border border-hvme-aurora-lilac/30 animate-pulse">
            {hours < 1 ? "⚡ FINAL MINUTES!" : "🔥 LESS THAN 24 HOURS!"}
          </span>
        </motion.div>
      )}
    </div>
  );
}

export default CountdownTimer;
